import type { TransformedTimeTrackingField } from '@atlassian/jira-business-fields/src/common/field-types/time-tracking/types.tsx';
import { useOriginalEstimateField } from '@atlassian/jira-issue-field-original-estimate/src/services';
import { TIME_ESTIMATE_TYPE } from '@atlassian/jira-platform-field-config';
import { createClipboardHelperComponent } from '../../../clipboard-handler/create-clipboard-helper-component';
import { useClipboardHandler } from '../../../clipboard-handler/use-clipboard-handler';
import {
	assertClipboardDataIsCompatible,
	assertClipboardHasPlainTextFormat,
} from '../../../clipboard-handler/utils';
import type {
	ClipboardItem,
	ListIssueFieldTransformed,
	PasteHandler,
	PasteMetadata,
	ListIssueForCopyPaste,
} from '../../../types';
import { timeTrackingSupportedTypes } from '../supported-field-types';
import type { TransformedTimeOriginalEstimate } from '../types';

const getPastedValue = (
	clipboardItem: ClipboardItem,
	metadata: PasteMetadata | null,
): number | null => {
	if (
		!assertClipboardHasPlainTextFormat(clipboardItem) ||
		!assertClipboardDataIsCompatible(metadata, timeTrackingSupportedTypes)
	) {
		return null;
	}

	const time = Number(clipboardItem.plainText);

	if (Number.isNaN(time)) {
		return null;
	}

	return time;
};

function assertTimeTrackingField(
	field: ListIssueFieldTransformed,
): asserts field is TransformedTimeTrackingField {
	if (!field) {
		throw new Error('Field is required');
	}

	if (field.type !== TIME_ESTIMATE_TYPE) {
		throw new Error('Field type is not supported');
	}
}

const useTimeTrackingClipboardHandler: PasteHandler = (
	issue: ListIssueForCopyPaste,
	field: ListIssueFieldTransformed,
) => {
	assertTimeTrackingField(field);

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const fieldValue = field.value as TransformedTimeOriginalEstimate | null;
	const hookInitialValue =
		(fieldValue?.timeAsString &&
			fieldValue?.timeInSeconds && {
				originalEstimate: fieldValue.timeAsString,
				originalEstimateSeconds: fieldValue.timeInSeconds,
			}) ||
		{};

	const [{ value }, { saveValue }] = useOriginalEstimateField({
		issueKey: issue.key,
		issueId: issue.issueId,
		fieldId: field.fieldId,
		timeTrackingValue: hookInitialValue,
	});

	return useClipboardHandler({
		issue,
		field,
		currentStringValue: typeof value === 'number' ? String(value) : null,
		currentValue: value,
		getPastedValue,
		saveValue,
	});
};

const TimeTrackingClipboardHelper = createClipboardHelperComponent(useTimeTrackingClipboardHandler);

export const timeTrackingClipboardHandler = {
	[TIME_ESTIMATE_TYPE]: TimeTrackingClipboardHelper,
};
